import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Pencil, Eye, Trash2, Plus, Star, Video } from "lucide-react";
import { api, fileUrl, formatINR } from "../lib/api";
import { toast } from "sonner";
import MyPageShell, { EmptyState } from "../components/MyPageShell";

const STATUS_STYLE = {
    active: "text-emerald-700 bg-emerald-100",
    pending: "text-amber-700 bg-amber-100",
    sold: "text-navy-500 bg-navy-500/10",
    expired: "text-red-700 bg-red-100",
};

function FeatureBadge({ property }) {
    if (property.featured) {
        return (
            <span data-testid={`my-prop-featured-badge-${property.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-extrabold bg-gradient-to-r from-gold-500 to-amber-500 text-navy-500">
                <Star className="w-3 h-3 fill-current" /> Featured
            </span>
        );
    }
    return null;
}

function FeatureAction({ property }) {
    if (property.featured) {
        const expIso = property.featured_expires_at;
        return (
            <div className="inline-flex items-center gap-1 text-[11px] text-gold-700 px-2 py-1 rounded border border-gold-500/40 bg-gold-500/5" data-testid={`my-prop-featured-until-${property.id}`}>
                <Star className="w-3 h-3 fill-current" />
                Featured{expIso ? ` · till ${new Date(expIso).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : ""}
            </div>
        );
    }
    return (
        <Link
            to={`/feature-property/${property.id}`}
            data-testid={`my-prop-feature-${property.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-extrabold text-navy-500 px-2 py-1 rounded bg-gold-500 hover:bg-gold-400"
        >
            <Star className="w-3 h-3" /> Feature
        </Link>
    );
}

/**
 * Two flavours of the same page:
 *  - MyProperties (default): only ACTIVE listings that appear on the marketplace
 *  - MyPostedProperties (posted): every property the user has ever posted (any status)
 */
export default function MyProperties({ posted = false }) {
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try {
            const params = posted ? {} : { status: "active" };
            const r = await api.get("/me/properties", { params });
            setItems(r.data || []);
        } catch {
            setItems([]);
        }
    };

    useEffect(() => { load(); }, [posted]);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this listing? This cannot be undone.")) return;
        setBusy(id);
        try {
            await api.delete(`/properties/${id}`);
            toast.success("Listing deleted");
            setItems((it) => it.filter((p) => p.id !== id));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete");
        } finally { setBusy(null); }
    };

    const title = posted ? "My Posted Properties" : "My Properties";
    const subtitle = posted
        ? "Every listing you've ever created — including pending, sold and expired."
        : "Your active listings live on the marketplace right now.";

    return (
        <MyPageShell title={title} subtitle={subtitle} testid={posted ? "my-posted-properties-page" : "my-properties-page"}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-navy-500/60">
                    {items === null ? "Loading…" : `${items.length} listing${items.length !== 1 ? "s" : ""}`}
                </div>
                <Link
                    to="/add-property"
                    data-testid="my-properties-add-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gold-500 text-navy-500 text-xs font-extrabold hover:bg-gold-400"
                >
                    <Plus className="w-3.5 h-3.5" /> Add new
                </Link>
            </div>

            {items === null && <div className="py-12 text-center text-sm text-navy-500/50">Loading your listings…</div>}
            {items && items.length === 0 && (
                <EmptyState
                    icon={Building2}
                    title="No listings yet"
                    message={posted ? "You haven't posted any property yet. List one for free and reach buyers instantly." : "You have no active listings on the marketplace."}
                    cta="Post a Property"
                    to="/add-property"
                    testid="my-properties-empty"
                />
            )}

            {items && items.length > 0 && (
                <ul className="space-y-3">
                    {items.map((p) => (
                        <li key={p.id} className="rounded-xl border border-navy-500/10 bg-white p-3 flex gap-3" data-testid={`my-prop-item-${p.id}`}>
                            <Link to={`/properties/${p.id}`} className="w-24 h-20 sm:w-28 sm:h-24 rounded-lg overflow-hidden bg-navy-500/8 shrink-0">
                                {p.images?.[0] ? (
                                    <img src={fileUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-navy-500/30">
                                        <Building2 className="w-8 h-8" />
                                    </div>
                                )}
                            </Link>
                            <div className="flex-1 min-w-0">
                                <Link to={`/properties/${p.id}`} className="block font-heading font-bold text-navy-500 text-sm truncate hover:text-gold-700">
                                    {p.title}
                                </Link>
                                <div className="text-[11px] text-navy-500/55 truncate">
                                    {p.locality ? `${p.locality}, ` : ""}{p.city}
                                </div>
                                <div className="mt-1 flex items-center gap-2 flex-wrap">
                                    <span className="text-gold-700 font-bold text-sm">{formatINR(p.price)}</span>
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${STATUS_STYLE[p.status] || "text-navy-500/60 bg-navy-500/10"}`}>
                                        {p.status || "active"}
                                    </span>
                                    <FeatureBadge property={p} />
                                </div>
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <FeatureAction property={p} />
                                    <Link to={`/properties/${p.id}`} data-testid={`my-prop-view-${p.id}`} className="inline-flex items-center gap-1 text-[11px] text-navy-500/70 hover:text-navy-500 px-2 py-1 rounded border border-navy-500/15">
                                        <Eye className="w-3 h-3" /> View
                                    </Link>
                                    <Link to={`/edit-property/${p.id}`} data-testid={`my-prop-edit-${p.id}`} className="inline-flex items-center gap-1 text-[11px] text-navy-500/70 hover:text-navy-500 px-2 py-1 rounded border border-navy-500/15">
                                        <Pencil className="w-3 h-3" /> Edit
                                    </Link>
                                    <Link
                                        to={`/reels/create?property=${p.id}`}
                                        data-testid={`my-prop-add-reel-${p.id}`}
                                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded font-extrabold shadow-sm"
                                        style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                                    >
                                        <Video className="w-3 h-3" /> Add Reel
                                    </Link>
                                    <button
                                        onClick={() => onDelete(p.id)}
                                        disabled={busy === p.id}
                                        data-testid={`my-prop-delete-${p.id}`}
                                        className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-700 px-2 py-1 rounded border border-red-200 disabled:opacity-50"
                                    >
                                        <Trash2 className="w-3 h-3" /> {busy === p.id ? "…" : "Delete"}
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </MyPageShell>
    );
}
